<template>
    <div class="product-grid-report">
        <div class="row main-content">
            <div class="sellers-content">
                <div class="row sellers_chart_wrapper">
                    <div
                        v-if="isChartLoading"
                        class="product-grid-report__update-loader"
                    >
                        <base-loader class="product-grid-report__update-loader-ring" />
                    </div>
                    <div class="row chart-header">
                        <div class="chart-dropdown">
                            <label>Compare to:</label>
                            <base-select
                                v-model="selectedMetric"
                                class="metric-select"
                                :options="[{label: 'Keyword Count', value: 'keyword_count'}, {label: 'MSV', value: 'msv_total'}, {label: 'Total Cards', value: 'total_cards'}]"
                                :show-top-border="true">
                            </base-select>
                        </div>
                    </div>
                    <GChart
                        :version="'42'"
                        :settings="{packages: ['corechart']}"
                        v-if="selectedSellers.length"
                        type="AreaChart"
                        :data="formattedSellerChartData"
                        :options="chartOptions"
                        :events="{
                            error: handleChartError
                        }"
                        class="sellers_chart"
                        ref="chart"
                    />
                    <div class="sellers_chart_no_data" v-else>
                        No stores selected
                    </div>
                </div>
                <div
                    class="row product-grid-report__advanced">
                    <advanced-filters :options="advancedOptions" 
                        :predefinedOptions="_advancedFilters" 
                        copyPage
                        exportEnabled
                        :regularFilterConfig="regularFilterConfig"
                        @share="copyFilters" 
                        @export="exportCsv"
                        @apply-advanced="applyAdvancedOptions" />
                </div>
                <div class="row sellers_table">
                    <div
                        v-if="isTableLoading"
                        class="product-grid-report__update-loader"
                    >
                        <base-loader class="product-grid-report__update-loader-ring" />
                    </div>
                    <div class="table-filters">
                        <div class="date-filter">
                            <label>Snapshot Date:</label>
                            <base-select
                                v-model="selectedSnapshotDate"
                                class="date-select date-select-primary"
                                :options="snapshots.map(date => ({label: date, value: date}))"
                                :show-top-border="true">
                            </base-select>
                        </div>
                        <div class="date-filter">
                            <label>Compare to:</label>
                            <base-select
                                v-model="compareSnapshotDate"
                                class="date-select date-select-compare"
                                :options="compareSnapshotDate ? [{label: 'Remove Comparison', value: null}, ...snapshots.map(date => ({label: date, value: date}))] : snapshots.map(date => ({label: date, value: date}))"
                                :show-top-border="true">
                            </base-select>
                        </div>
                    </div>
                    <div id="tabulator-sellers" class="table table-sellers"></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import { baseComponent } from '@/features/reports/common/Mixins'
import BaseSortMixins from '@/common/BaseSortMixins'
import { mapActions, mapState } from 'vuex'
import filters from '@/common/Filters'
import { GChart } from 'vue-google-charts'
import Tabulator from 'tabulator-tables'
import BaseLoader from '@/common/BaseLoader'

import {
    addOptionsToConfig,
    ADVANCED_OPTION_TYPE,
    convertAdvancedOptionsToFilters,
    productGridFieldsConfig,
} from '@/features/reports/common/advanced-options/config'
import AdvancedFilters from '@/features/reports/common/advanced-filters/AdvancedFilters'
import { advancedFiltersMixins } from '@/features/reports/common/AdvancedFiltersMixins'

const ADVANCED_OPTIONS_CONFIG = productGridFieldsConfig

const REGULAR_FILTER_CONFIG = [
    {
        type: 'attribute',
        searchPlaceholder: 'Search attributes',
        listTitle: 'Include the following attributes:'
    },
    {
        type: 'urlAttribute',
        searchPlaceholder: 'Search url attributes',
        listTitle: 'Include the following url attributes:'
    },
    {
        type: 'pageType',
        searchPlaceholder: 'Search page types',
        listTitle: 'Include the following page types:'
    },
    {
        type: 'section',
        searchPlaceholder: 'Search sections',
        listTitle: 'Include the following sections:'
    },
    {
        type: 'gridNumber',
        searchPlaceholder: 'Search grid numbers',
        listTitle: 'Include the following grid numbers:'
    },
]

export default {
    name: 'ProductGridReportProgress',
    filters: {
        ...filters
    },
    components: { AdvancedFilters, GChart, BaseLoader },
    mixins: [baseComponent, BaseSortMixins, advancedFiltersMixins],
    props: ['filters'],
    created() {
        if (this.selectedClient) {
            this.loadPageData()
        }
    },
    data() {
        return {
            selectedAdvancedOptions: [],
            advancedOptionsConfig: ADVANCED_OPTIONS_CONFIG,
            regularFilterConfig: REGULAR_FILTER_CONFIG,
            table: null,
            isChartLoading: false,
            isTableLoading: false,
            selectedSnapshotDate: null,
            compareSnapshotDate: null,
            selectedSellers: [],
            competitorColors: {
                // This will be populated when competitors are loaded
            },
            selectedMetric: 'keyword_count',
            firstLoad: true,
        }
    },
    computed: {
        ...mapState('productGridReport', [
            'sellerChartData',
            'sellerTableData',
            'clientShop',
            'competitorShops'
        ]),
        ...mapState(['selectedClient']),
        ...mapState('rankZoneCommon', {
            snapshots: state => state.snapshots
        }),
        formattedSellerChartData() {
            if (!this.sellerChartData || !this.selectedSellers.length) {
                return [['Date', 'Keywords']];
            }

            // Create header row
            // const header = ['Date', this.selectedMetric === 'keyword_count' ? 'Keywords' : 'MSV', ...this.selectedSellers];
            const header = [
                { type: 'date', label: 'Date' },
                { type: 'number', label: this.selectedMetric === 'keyword_count' ? 'Keywords' : 'MSV' },
                ...this.selectedSellers.map(seller => ({ type: 'number', label: seller }))
            ];
            
            // Get all unique dates from data
            const allDates = new Set();
            this.sellerChartData.chart_totals?.forEach(item => allDates.add(item.snapshot_date));
            this.sellerChartData.chart_sellers?.forEach(item => allDates.add(item.snapshot_date));
            
            // Group data by date
            const dataByDate = {};
            allDates.forEach(date => {
                dataByDate[date] = {
                    date: date,
                    keywordCount: 0,
                    msvTotal: 0,
                    totalCards: 0,
                    sellers: {}
                };
                // Initialize all sellers to 0 for this date
                this.selectedSellers.forEach(seller => {
                    dataByDate[date].sellers[seller] = 0;
                });
            });

            // Fill in keyword counts and MSV from totals
            this.sellerChartData.chart_totals?.forEach(item => {
                const date = item.snapshot_date;
                if (dataByDate[date]) {
                    dataByDate[date].keywordCount = Number(item.keyword_count);
                    dataByDate[date].msvTotal = Number(item.msv_total);
                    dataByDate[date].totalCards = Number(item.total_cards);
                }
            });

            // Fill in seller appearances from sellers data
            this.sellerChartData.chart_sellers?.forEach(item => {
                const date = item.snapshot_date;
                if (dataByDate[date] && this.selectedSellers.includes(item?.seller)) {
                    dataByDate[date].sellers[item.seller] = Number(item.seller_appearances);
                }
            });

            // Convert to array format
            const rows = Object.values(dataByDate)
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .map(item => {
                    // should be date without time
                    // const row = [{
                    //     v: new Date(item.date),
                    //     // f: new Date(item.date).toLocaleDateString()
                    //     f: item.date
                    // }, this.selectedMetric === 'keyword_count' ? item.keywordCount : item.msvTotal]
                    let metricValue;
                    if (this.selectedMetric === 'keyword_count') {
                        metricValue = item.keywordCount;
                    } else if (this.selectedMetric === 'msv_total') {
                        metricValue = item.msvTotal;
                    } else if (this.selectedMetric === 'total_cards') {
                        metricValue = item.totalCards;
                    }
                    const row = [new Date(item.date), metricValue];
                    // const row = [new Date(item.date), this.selectedMetric === 'keyword_count' ? item.keywordCount : item.msvTotal];
                    this.selectedSellers.forEach(seller => {
                        row.push(item.sellers[seller]);
                    });
                    return row;
                });

            return [header, ...rows];
        },
        filteringCells() {
            return this.tableConf.cells.slice(1, this.numberColumnsAmount)
        },
        numberColumnsAmount() {
            return this.tableConf.cells.length - 1
        },
        filteredSnapshots() {
            return this.productGridSellerReport || []
        },
        ...mapState('rankZoneCommon', {
            advancedOptions(state) {
                const gridNumberOptions = [
                    { label: '1', value: '1', _id: '1' },
                    { label: '2', value: '2', _id: '2' },
                    { label: '3', value: '3', _id: '3' },
                    { label: '4+', value: '4+', _id: '4+' },
                ]
                let config = _.cloneDeep(this.advancedOptionsConfig)
                addOptionsToConfig(config, state.sections, ADVANCED_OPTION_TYPE.SECTION)
                addOptionsToConfig(config, gridNumberOptions, ADVANCED_OPTION_TYPE.GRID_NUMBER)
                addOptionsToConfig(config, state.attributes, ADVANCED_OPTION_TYPE.ATTRIBUTE)
                // addOptionsToConfig(config, state.urlStatuses, ADVANCED_OPTION_TYPE.URL_STATUS)
                addOptionsToConfig(config, state.pageTypes, ADVANCED_OPTION_TYPE.PAGE_TYPE)
                // addOptionsToConfig(config, state.scores, ADVANCED_OPTION_TYPE.SCORE)
                // addOptionsToConfig(config, state.isOptimized, ADVANCED_OPTION_TYPE.IS_OPTIMIZED)
                addOptionsToConfig(config, state.urlAttributes, ADVANCED_OPTION_TYPE.URL_ATTRIBUTE)
                // addOptionsToConfig(config, state.serpFeature, ADVANCED_OPTION_TYPE.SERP_FEATURE)
                // addOptionsToConfig(config, state.contentBriefStatuses, ADVANCED_OPTION_TYPE.CONTENT_BRIEF_STATUS)
                // addOptionsToConfig(config, state.mappedUrls, ADVANCED_OPTION_TYPE.URL)
                // addOptionsToConfig(config, state.mismatchedUrls, ADVANCED_OPTION_TYPE.MISMATCHED_URLS)
                
                return state.difficultyScoreEnabled ? config : config.filter(i => i.type !== ADVANCED_OPTION_TYPE.DIFF_SCORE)
            },
        }),
        tableConfig() {
            const scope = this

            const tableSorter = (a, b, aRow, bRow, column, dir, sorterParams) => {
                // Handle totals row first
                if (aRow.getData().seller === 'totals_row') return -1;
                if (bRow.getData().seller === 'totals_row') return 1;


                const aData = aRow.getData();
                const bData = bRow.getData();

                if (this.compareSnapshotDate) {
                    const tableData = this.table.getData().filter(row => row.isPrimary);
                    tableData.sort((a, b) => {
                        const strA = a[column.getField()].toString().toLowerCase();
                        const strB = b[column.getField()].toString().toLowerCase();

                        const numA = parseFloat(strA);
                        const numB = parseFloat(strB);

                        const isNumA = !isNaN(numA);
                        const isNumB = !isNaN(numB);

                        let result;

                        if (isNumA && isNumB) {
                            result = numA - numB;
                        } else if (isNumA) {
                            result = -1;
                        } else if (isNumB) {
                            result = 1;
                        } else {
                            result = strA.localeCompare(strB);
                        }

                        return dir === 'asc' ? result : -result;
                    })
                    // now it should sort in the same order as the tableData
                    const tableDataFields = tableData.map(row => row.seller)
                    return dir === 'asc' 
                        ? tableDataFields.indexOf(aData.seller) - tableDataFields.indexOf(bData.seller)
                        : tableDataFields.indexOf(bData.seller) - tableDataFields.indexOf(aData.seller);
                }

                // Finally sort by the column value
                if (typeof a === 'number' && typeof b === 'number') {
                    return a - b;
                }

                // Handle string sorting
                const aStr = String(a || '').toLowerCase();
                const bStr = String(b || '').toLowerCase();
                return aStr.localeCompare(bStr);
            }
            return {
                data: [],
                // height: '100%',
                initialSort: [
                    {column: 'keyword_count', dir: 'desc'},
                    {column: 'is_client_shop', dir: 'desc'},
                ],
                rowSelectionChanged: function(data, rows) {
                    // Filter out totals row and non-primary rows when comparing
                    const selectedRows = data.filter(row => {
                        if (row?.seller === 'totals_row') return false;
                        if ('isPrimary' in row && scope.compareSnapshotDate) {
                            return row.isPrimary
                        }
                        return true;
                    });
                    scope.$set(scope, 'selectedSellers', selectedRows.map(row => row?.seller));

                    // Reformat all rows to update their display state
                    this.getRows().forEach(row => row.reformat());
                },
                selectable: true,
                selectableRollingSelection: true,
                index: 'seller',
                dataLoaded:function(data){ //freeze first row on data load
                    const firstRow = this.getRows()[0];

                    if(firstRow){
                        firstRow.freeze();
                    }

                    // If compareSnapshotDate is set, freeze the second row
                    // this second row should not have a checkbox since isPrimary is false
                    if (scope.compareSnapshotDate) {
                        const secondRow = this.getRows()[1];
                        if (secondRow) {
                            secondRow.freeze();
                            secondRow.selectable = false;
                        }
                    }
                },
                columns: [
                    {
                        title: 'Snapshot Date',
                        field: 'snapshot_date',
                        sorter: 'date',
                        visible: false,
                        download: true,
                    },
                    {
                        title: 'Store',
                        field: 'seller',
                        sorter: tableSorter,
                        widthGrow: 1,
                        formatter: function(cell, formatterParams, onRendered) {
                            const row = cell.getRow();
                            const value = cell.getValue();
                            const isSelected = row.isSelected();
                            const data = row.getData();

                            const div = document.createElement("div");
                            div.style.display = "flex";
                            div.style.alignItems = "center";
                            div.style.gap = "8px";

                            // Add vertical color strip
                            const colorStrip = document.createElement("div");
                            colorStrip.style.width = "4px";
                            colorStrip.style.height = "32px";
                            colorStrip.style.marginBottom = "0";
                            colorStrip.style.marginTop = "0";
                            colorStrip.style.marginRight = "8px";
                            colorStrip.style.padding = "0";
                            if (value !== 'totals_row') {
                                colorStrip.style.backgroundColor = scope.sellersChartColorMapping.find(series => series.name === value)?.color;
                            }
                            div.appendChild(colorStrip);

                            // Create checkbox wrapper for consistent spacing
                            const checkboxWrapper = document.createElement("div");
                            checkboxWrapper.style.width = "20px";
                            checkboxWrapper.style.marginRight = "8px";

                            // Show select-all checkbox only for primary totals row
                            if (value === 'totals_row' && (!('isPrimary' in data) || data.isPrimary)) {
                                // Add select-all checkbox for totals row
                                const selectAllCheckbox = document.createElement("input");
                                selectAllCheckbox.type = "checkbox";
                                selectAllCheckbox.style.cursor = "pointer";
                                selectAllCheckbox.className = 'table-select-checkbox';
                                
                                // Check if all selectable rows are selected
                                const allRows = scope.table.getRows();
                                const selectableRows = allRows.filter(row => {
                                    const data = row.getData();
                                    return data?.seller !== 'totals_row' && 
                                           (!('isPrimary' in data && scope.compareSnapshotDate) || data.isPrimary);
                                });
                                selectAllCheckbox.checked = selectableRows.length > 0 && 
                                                          selectableRows.every(row => row.isSelected());

                                selectAllCheckbox.addEventListener("click", function(e) {
                                    e.stopPropagation();
                                    selectableRows.forEach(row => {
                                        if (selectAllCheckbox.checked) {
                                            row.select();
                                        } else {
                                            row.deselect();
                                        }
                                    });
                                });

                                checkboxWrapper.appendChild(selectAllCheckbox);
                            } else {
                                // Regular row checkbox
                                const checkbox = document.createElement("input");
                                checkbox.type = "checkbox";
                                checkbox.style.cursor = "pointer";
                                checkbox.className = 'table-select-checkbox';
                                checkbox.checked = isSelected;

                                checkbox.addEventListener("click", function(e) {
                                    e.stopPropagation();
                                    if (checkbox.checked) {
                                        row.select();
                                    } else {
                                        row.deselect();
                                    }
                                });

                                if ('isPrimary' in data && scope.compareSnapshotDate) {
                                    if (!data.isPrimary) {
                                        checkbox.style.display = 'none';
                                    }
                                }
                                checkboxWrapper.appendChild(checkbox);
                            }
                            div.appendChild(checkboxWrapper);

                            // Add circle if isPrimary field exists
                            if ('isPrimary' in data && scope.compareSnapshotDate) {
                                const circle = document.createElement("div");
                                circle.style.width = "8px";
                                circle.style.height = "8px";
                                circle.style.borderRadius = "50%";
                                circle.style.backgroundColor = data.isPrimary ? "#2EC4B6" : "#2A3BCF";
                                div.appendChild(circle);
                            }

                            const span = document.createElement("span");
                            span.textContent = value === 'totals_row' ? 'Total' : value;
                            div.appendChild(span);

                            return div;
                        },
                        cellClick: function(e, cell) {
                            const row = cell.getRow();
                            const data = row.getData();
                            
                            // Don't allow selection for totals row
                            if (data?.seller === 'totals_row') return;
                            
                            // Only allow selection for primary rows when comparing
                            if (!('isPrimary' in data && scope.compareSnapshotDate) || data.isPrimary) {
                                if (row.isSelected()) {
                                    row.deselect();
                                } else {
                                    row.select();
                                }
                            }
                        }
                    },
                    {
                        title: 'Keyword Count',
                        sorter: tableSorter,
                        field: 'keyword_count',
                        formatter: 'money',
                        formatterParams: {
                            thousand: ',',
                            precision: false
                        }
                    },
                    {
                        title: 'is_client_shop',
                        field: 'is_client_shop',
                        sorter: 'boolean',
                        visible: false,
                    },
                    {
                        title: 'MSV Total',
                        field: 'msv_total',
                        sorter: tableSorter,
                        formatter: 'money',
                        formatterParams: {
                        thousand: ',',
                        precision: false
                        },
                    },
                    {
                        title: 'Total Cards',
                        field: 'total_cards',
                        sorter: tableSorter,
                        formatter: 'money',
                        formatterParams: {
                            thousand: ',',
                            precision: false
                        }
                    },
                    {
                        title: 'Store\'s Cards',
                        field: 'seller_cards',
                        sorter: tableSorter,
                        formatter: 'money',
                        formatterParams: {
                            thousand: ',',
                            precision: false
                        }
                    },
                ],
                layout: 'fitColumns',
                tooltipsHeader: true,
            }
        },
        sellersChartColorMapping() {
            const otherColors = ['#9E0142',
            '#D53E4F',
            '#F46D43',
            '#FDAE61',
            '#FEE08B',
            '#E6F598',
            '#ABDDA4',
            '#3288BD',
            '#5E4FA2',
            '#B3509D'
                    ]
            const keywordColor = '#F4F6FC'
            const clientShopColor = '#2EC4B6'
            // const competitorColorOptions = [keywordColor, clientShopColor, '#FF6B6B', '#4A90E2', '#50E3C2', '#F5A623', '#7ED321', '#9013FE', '#417505', '#B8E986', '#D0021B', '#FFB347'];
            const competitorColorOptions = [keywordColor, clientShopColor, ...otherColors];


            const allSeries = ['Keywords', this.clientShop, ...this.competitorShops]
            return allSeries.map((series, index) => {
                return { color: competitorColorOptions[index], name: series };
            })
        },
        chartOptions() {
            
            const series = {};
            
            series[0] = { targetAxisIndex: 1, areaOpacity: 1, color: this.sellersChartColorMapping.find(series => series.name === 'Keywords')?.color };
            
            // this should not sort at all
            this.selectedSellers?.forEach((_, index) => {
                series[index + 1] = { targetAxisIndex: 0, areaOpacity: 0, color: this.sellersChartColorMapping.find(series => series.name === _)?.color  };
            });
            let ticks = this.formattedSellerChartData.slice(1).map(row => row[0])
            function getEvenlySpacedDates(start, end) {
                const interval = (end - start) / 4;

                return [1, 2, 3].map(i => new Date(start?.getTime() + i * interval));
            }
            const targets = getEvenlySpacedDates(ticks[0], ticks[ticks.length - 1])

            function findClosestDates(targetDates, candidateDates) {
                return targetDates.map(target => {
                    let closest = candidateDates[0];
                    let minDiff = Math.abs(target - closest);

                    for (let i = 1; i < candidateDates.length; i++) {
                    const diff = Math.abs(target - candidateDates[i]);
                    if (diff < minDiff) {
                        closest = candidateDates[i];
                        minDiff = diff;
                    }
                    }

                    return closest;
                });
            }
            ticks = [ticks[0], ...findClosestDates(targets, ticks), ticks[ticks.length - 1]]
            // remove duplicates
            ticks = ticks.filter((tick, index, self) => self.indexOf(tick) === index)


            return {
                // legend: { position: 'none' }, TESTING below
                legend: { position: 'top', textStyle: { fontSize: 14 } },
                series,
                vAxes: {
                    0: { title: 'Store\'s Cards', minValue: 0 },
                    1: { title: this.selectedMetric === 'keyword_count' ? 'Keywords' : 'MSV', minValue: 0 }
                },
                hAxis: {
                    minorGridlines: { count: 0 },
                    gridlines: { 
                        color: 'transparent'
                     },
                    format: 'M/d/yyyy',
                    ticks: ticks
                },
                chartArea: {
                    width: '80%',
                    height: '80%'
                },
                height: 400,
                width: '100%',
                // colors,
                crosshair: {
                    trigger: 'both',
                    orientation: 'vertical',
                    color: '#cccccc',
                    focused: {
                        color: '#cccccc',
                        opacity: 0.8
                    }
                },
                focusTarget: 'category',
                vAxis: {
                    minorGridlines: { count: 0 },
                    viewWindow: { min: 0 }
                },
            }
        }
    },
    watch: {
        selectedClient(client) {
            if (!client) {
                return
            }
            this.isDataFetched = false
            this.isDataUpdating = true
            
            // Get sellers list first
            const filters = Object.assign({}, this.filterParams, { 
                advancedFilters: convertAdvancedOptionsToFilters(this.selectedAdvancedOptions),
                snapshot_date: this.selectedSnapshotDate
            })
            this.getSellersList({
                clientId: this.clientId,
                filters
            }).then(() => {
                this.loadPageData()
            })
        },
        sellerTableData: {
            async handler(newValue, oldValue) {
                this.isDataUpdating = true
                await this.loadTableData()
                this.isDataUpdating = false
            },
            deep: true,
        },
        snapshots: {
            handler(newSnapshots) {
                if (newSnapshots && newSnapshots.length > 0) {
                    this.selectedSnapshotDate = newSnapshots[0]
                }
            },
            immediate: true
        },
        selectedAdvancedOptions: {
            async handler() {
                if (this.isDataFetched) {
                    this.isDataUpdating = true
                    await this.updateData()
                    this.isDataUpdating = false
                }
            },
            deep: true
        },
        selectedSnapshotDate: {
            async handler() {
                if (!this.isDataFetched) return;
                this.isTableLoading = true;
                await this.getSellerTableData({
                    clientId: this.clientId,
                    filters: {
                        snapshot_date: this.selectedSnapshotDate
                    }
                })
                await this.loadTableData()
                this.isTableLoading = false;
            }
        },
        compareSnapshotDate: {
            async handler(newValue, oldValue) {
                if (!this.isDataFetched) return;
                this.isTableLoading = true;
                await this.getSellerTableData({
                    clientId: this.clientId,
                    filters: {
                        snapshot_date: this.compareSnapshotDate
                    }
                })
                await this.loadTableData()
                let currentSorters = this.table.getSorters();
                currentSorters = currentSorters.filter(sorter => sorter.field !== 'is_client_shop');
                this.table.setSort(currentSorters);
                this.isTableLoading = false;
            }
        },
        competitorShops: {
            handler(newCompetitors) {
                // Reset competitor colors when competitors change
                this.competitorColors = {};
            },
            immediate: true
        }
    },
    methods: {
        ...mapActions('productGridReport', [
            'getSellerChartData',
            'getSellerTableData',
            'getSellersList'
        ]),
        exportCsv() {
            const frozenRows = this.table.getRows().filter(row => row.isFrozen());
            frozenRows.forEach(row => {
                row.unfreeze()
            });
            let downloadName = `${this.selectedClient.name}_${this.selectedSnapshotDate}`
            if (this.compareSnapshotDate) {
                downloadName += `_${this.compareSnapshotDate}`
            }
            this.table.download('csv', `${downloadName}.csv`)
            frozenRows.forEach(row => {
                row.freeze()
            });
        },
        handleChartError(error) {
            google.visualization.errors.removeError(error.id);
            console.error('vue-google-charts error:', error)
        },
        async loadPageData() {
            const advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
            const filters = Object.assign({}, this.filterParams, { 
                advancedFilters,
                snapshot_date: this.selectedSnapshotDate
            })
            
            // Set loading states
            this.isChartLoading = true
            this.isTableLoading = true
            
            // Load all data
            const requests = [
                this.getSellerChartData({
                    clientId: this.clientId,
                    filters
                }).finally(() => {
                    this.isChartLoading = false
                }),
                this.getSellerTableData({
                    clientId: this.clientId,
                    filters
                }).finally(() => {
                    this.isTableLoading = false
                })
            ]
            if (!this.clientShop) {
                requests.push(this.getSellersList({
                    clientId: this.clientId,
                    filters
                }))
            }
            await Promise.all(requests)
            await this.loadTableData()
            
            this.isDataFetched = true
        },
        async updateData() {
            await this.loadPageData()
        },
        async applyAdvancedOptions(options) {
            this.selectedAdvancedOptions = _.cloneDeep(options)
        },
        onWebPropertyGroupChanged(group) {
            this.webPropertyGroupSelected = group
            this.updateData()
        },
        async loadTableData() {
            const selectedSellers = _.cloneDeep(this.selectedSellers)
            if (!this.sellerTableData || !this.selectedSnapshotDate) return;
            
            // Get data for the selected snapshot date
            let currentData = [], compareData = [];
            currentData = (this.sellerTableData[this.selectedSnapshotDate] || [])

            if (this.compareSnapshotDate) {
                compareData = (this.sellerTableData[this.compareSnapshotDate] || [])
            }
            const sellers = [this.clientShop, ...this.competitorShops]
            // tableData is an array of objects from the data array, one per seller, if there is no entry for a seller, it is added with all fields set to 0
            currentData = sellers.map(seller => {
                const sellerData = currentData.find(row => row?.seller === seller)
                if (this.compareSnapshotDate && sellerData) {
                    sellerData.isPrimary = true
                }
                return sellerData || {
                    seller,
                    keyword_count: 0,
                    msv_total: 0,
                    total_cards: 0,
                    seller_cards: 0,
                    isPrimary: this.compareSnapshotDate ? true : false
                }
            })
            if (this.compareSnapshotDate && this.selectedSnapshotDate !== this.compareSnapshotDate) {
                compareData = sellers.map(seller => {
                    const sellerData = compareData.find(row => row?.seller === seller)
                    if (this.compareSnapshotDate && sellerData) {
                        sellerData.isPrimary = false
                    }
                    return sellerData || {
                        seller,
                        keyword_count: 0,
                        msv_total: 0,
                        total_cards: 0,
                        seller_cards: 0,
                        isPrimary: this.compareSnapshotDate ? false : true
                    }
                })
            } 
            let currentTotalsRow = (this.sellerTableData[this.selectedSnapshotDate] || [])
            currentTotalsRow = currentTotalsRow.find(row => row?.seller === 'totals_row') || {
                seller: 'totals_row',
                keyword_count: 0,
                msv_total: 0,
                total_cards: 0,
                seller_cards: 0,
                isPrimary: true
            }
            let compareTotalsRow = (this.sellerTableData[this.compareSnapshotDate] || [])
            compareTotalsRow = compareTotalsRow.find(row => row?.seller === 'totals_row') || {
                seller: 'totals_row',
                keyword_count: 0,
                msv_total: 0,
                total_cards: 0,
                seller_cards: 0,
                isPrimary: false
            }
            let tableData = []
            if (this.compareSnapshotDate) {
                currentTotalsRow.isPrimary = true
                compareTotalsRow.isPrimary = false
                tableData = [currentTotalsRow, compareTotalsRow, ...currentData, ...compareData]
            } else {
                tableData = [currentTotalsRow, ...currentData]
            }
            this.table.setData(tableData);
            
            // On initial load, select only the first 5 sellers if none are selected
            if (!selectedSellers.length && this.firstLoad) {
                this.firstLoad = false;
                // this should sort tableData by is_client_shop, then by keyword_count, exclude totals_row
                const sortedTableData = tableData.filter(row => row.seller !== 'totals_row').sort((a, b) => {
                    if (a.is_client_shop && !b.is_client_shop) return -1;
                    if (!a.is_client_shop && b.is_client_shop) return 1;
                    return b.keyword_count - a.keyword_count;
                }).slice(0, 5)
                sortedTableData.forEach((row, i) => {
                    this.table.selectRow(row?.seller);
                });
            } else {
                // Otherwise select only the previously selected sellers
                this.table.selectRow(currentData.filter(row => 
                    selectedSellers.includes(row?.seller)
                ).map(row => row?.seller));
                // Reformat all rows to update checkbox states
                this.table.getRows().forEach(row => row.reformat());
            }
            this.table.getRows().forEach(row => row.reformat());
        },
    },
    mounted() {
        this.table = new Tabulator('#tabulator-sellers', this.tableConfig)
    }
}
</script>

<style scoped lang="scss">
.container {
    display: flex;
    flex-direction: column;
    width: 90vw;
    margin: 0 auto;
}

.section-row {
    border-bottom: 2px solid $rfx-light-steel;
    display: flex;
    flex-direction: row;
    height: 40px;
    width: 100%;
}


.section-filler {
    width: 100%;
}

.row {
    display: flex;
    flex-direction: row;
}

.product-grid-report {
    min-width: 1200px;
    position: relative;
    min-height: 200vh !important;

    &__advanced {
        margin-top: 30px;
        margin-bottom: 14px;
        position: relative;

        &::v-deep .collapsible__header {
            padding-right: 80px !important;
        }

        &::v-deep .collapsible__arrow {
            right: 66px !important;
        }
    }
}

.main-content {
    width: 100%;
    display: flex;
    flex-direction: column;
}

.sellers-content {
}
.sellers_chart_wrapper {
    width: 100%;
    height: 420px;
    border-radius: 4px;
    border: 1px solid $rfx-light-steel;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 0;
    position: relative;

    .product-grid-report__update-loader {
        position: absolute;
        display: flex;
        width: 100%;
        height: 100%;
        justify-content: center;
        align-items: center;
        background-color: rgba(255, 255, 255, 0.8);
        top: 0;
        left: 0;
        z-index: 1000;
        border-radius: inherit;
    }

    .product-grid-report__update-loader-ring {
        margin: 0;
    }
}
.sellers_chart_no_data {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}
.sellers_chart {
    width: 100%;
    height: 100%;
}
.table-sellers {
    width: 90%;
    margin: 20px 40px;
}
.sellers_table {
    flex-direction: column;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    background-color: #fff;
    margin-top: 10px;
    border-radius: 4px;
    border: 1px solid $rfx-light-steel;
    position: relative;

    .product-grid-report__update-loader {
        position: absolute;
        display: flex;
        width: 100%;
        height: 100%;
        justify-content: center;
        align-items: center;
        background-color: rgba(255, 255, 255, 0.8);
        top: 0;
        left: 0;
        z-index: 1000;
        border-radius: inherit;
    }

    .product-grid-report__update-loader-ring {
        margin: 0;
    }
}

.keywords_chart {
    width: 100%;
    height: 400px;
    background-color: #fff;
    border-radius: 4px;
    border: 1px solid $rfx-light-steel;
}
.keywords_table {
    width: 100%;
    height: 400px;
    background-color: #fff;
    margin-top: 10px;
    border-radius: 4px;
    border: 1px solid $rfx-light-steel;
}

/* Tabulator styles */
::v-deep(.tabulator .tabulator-cell) {
    border-right: none !important;
}

::v-deep(.tabulator .tabulator-header .tabulator-col) {
    border-right: none !important;
}

::v-deep(.tabulator-cell) {
    height: 40px !important;
    align-content: center !important;
}

::v-deep(.tabulator-row .tabulator-cell:last-child),
::v-deep(.tabulator-header .tabulator-col:last-child) {
    border-right: none !important;
}

::v-deep(.tabulator-paginator button.active) {
    color: $rfx-dark-march !important;
    border-bottom: 2px solid !important;
    border-radius: 0 !important;
    border-color: $rfx-bright-turquoise !important;
}

::v-deep(.tabulator-paginator button) {
    border-radius: 4px;
    padding: 4px 8px;
    margin: 0 2px;
    border: none !important;
    background-color: white;
    color: $rfx-dark-march !important;
    transition: all 0.2s;
}

::v-deep(.tabulator-row.tabulator-selected) {
    background-color: inherit !important;
}

::v-deep(.table-select-checkbox) {
    appearance: none;
    -webkit-appearance: none;
    width: 20px;
    height: 20px;
    border: 2px solid $rfx-light-steel;
    border-radius: 2px;
    margin-right: 8px;
    position: relative;
    cursor: pointer;
    transition: all 0.2s ease;

    &:checked {
        background-color: #fff;
        border-color: $rfx-light-steel;

        &::after {
            content: '';
            position: absolute;
            left: 5px;
            top: 2px;
            width: 4px;
            height: 8px;
            border: solid $rfx-bright-turquoise;
            border-width: 0 2px 2px 0;
            transform: rotate(45deg);
        }
    }

    &:disabled {
        background-color: $rfx-light-steel;
        border-color: $rfx-light-steel;
        cursor: not-allowed;
    }
}

.chart-header {
    width: 100%;
    justify-content: flex-end;
}

.chart-dropdown {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 40%;

    label {
        color: $rfx-dark-march;
        font-weight: 500;
    }

    .metric-select {
        padding: 4px 8px;
        width: 50%;
        border-radius: 4px;
        background-color: white;
        color: $rfx-dark-march;
        font-size: 14px;
        cursor: pointer;
        
        &:focus {
            outline: none;
            border-color: $rfx-medium-march;
        }
    }
}

.table-filters {
    display: flex;
    justify-content: flex-start;
    gap: 20px;
    padding: 20px 40px 0;
    width: 90%;
}

.date-filter {
    display: flex;
    align-items: center;
    gap: 10px;

    label {
        color: $rfx-dark-march;
        font-weight: 500;
    }

    .date-select-primary {
        width: 200px;

        ::v-deep .select__selection {
            position: relative;
            margin-left: 10px;

            &::before {
                content: '';
                position: absolute;
                left: 0;
                top: 50%;
                transform: translateY(-50%);
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background-color: #2EC4B6;
            }
        }
    }
    .date-select-compare {
        width: 200px;

        ::v-deep .select__selection {
            position: relative;
            margin-left: 10px;

            &::before {
                content: '';
                position: absolute;
                left: 0;
                top: 50%;
                transform: translateY(-50%);
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background-color: #2A3BCF;
            }
        }
    }
}
</style> 